import React from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function OptionButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left px-5 py-4 rounded-xl border transition-all duration-200 text-sm sm:text-base leading-relaxed",
        selected
          ? "border-primary bg-primary/10 text-foreground shadow-sm"
          : "border-border bg-card text-foreground/80 hover:border-input hover:bg-muted"
      )}
    >
      <span className="flex items-start gap-3">
        <span
          className={cn(
            "mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-semibold",
            selected
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input text-muted-foreground"
          )}
        >
          {selected ? "✓" : ""}
        </span>
        <span>{children}</span>
      </span>
    </button>
  );
}

export default function QuizQuestion({
  question,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack
}) {
  const isAnswered =
    question.type === "scenario"
      ? answer && answer.trim().length > 0
      : answer !== null && answer !== undefined;

  return (
    <div className="max-w-xl mx-auto px-6 py-10 sm:py-16">
      <div className="flex items-center justify-between mb-8">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          Question {index + 1} of {total}
        </span>
        <span className="text-xs text-muted-foreground">
          {question.type === "multiple_choice" && "Multiple choice"}
          {question.type === "true_false" && "True / False"}
          {question.type === "scenario" && "Short scenario"}
        </span>
      </div>

      <div className="w-full h-1.5 bg-muted rounded-full mb-10 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug">
        {question.question}
      </h2>

      <div className="mt-8 space-y-3">
        {question.type === "multiple_choice" &&
          question.options.map((opt, i) => (
            <OptionButton
              key={i}
              selected={answer === i}
              onClick={() => onAnswer(i)}
            >
              {opt}
            </OptionButton>
          ))}

        {question.type === "true_false" && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "True", value: true },
              { label: "False", value: false }
            ].map((opt) => (
              <OptionButton
                key={String(opt.value)}
                selected={answer === opt.value}
                onClick={() => onAnswer(opt.value)}
              >
                {opt.label}
              </OptionButton>
            ))}
          </div>
        )}

        {question.type === "scenario" && (
          <textarea
            value={answer || ""}
            onChange={(e) => onAnswer(e.target.value)}
            rows={6}
            placeholder="Write how you'd respond, step by step..."
            className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm sm:text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none transition"
          />
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={index === 0}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!isAnswered}
          className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          {index === total - 1 ? "Finish" : "Next"}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}